import { DRIVERS, DRIVER_INTERPRETATION } from "@/data/idr";

const L = (lang, fr, es) => (lang === "es" ? es : fr);

function driverInterpretation(score, lang) {
  const r = DRIVER_INTERPRETATION.find((x) => score >= x.min && score <= x.max) || DRIVER_INTERPRETATION[0];
  return { min: r.min, max: r.max, color: r.color, label: r[lang] };
}

function ecartReading(gap, lang) {
  if (gap <= 10) return L(lang, "un fonctionnement relativement flexible", "un funcionamiento relativamente flexible");
  if (gap <= 20) return L(lang, "un style préférentiel", "un estilo preferente");
  return L(lang, "une spécialisation importante autour d'un driver", "una especialización importante en torno a un driver");
}

// answers: { itemNumber(1..50): 0 | 25 | 50 | 75 | 100 }
// Score par driver = moyenne des 10 réponses, exprimée en pourcentage (0–100).
export function computeIdrResults(answers, lang = "fr") {
  const rows = DRIVERS.map((d) => {
    const sum = d.items.reduce((s, n) => s + (Number.isFinite(answers[n]) ? answers[n] : 0), 0);
    const score = Math.round(sum / d.items.length);
    return {
      key: d.key,
      name: d.name[lang],
      short: d.name[lang],
      description: d.description[lang],
      color: d.color,
      score, // %
      interp: driverInterpretation(score, lang),
    };
  });

  const sorted = [...rows].sort((a, b) => b.score - a.score);
  const principal = sorted[0];
  const second = sorted[1];
  const gap = principal.score - second.score;

  return {
    rows,
    sorted,
    principal,
    second,
    gap,
    ecart: ecartReading(gap, lang),
    radarData: rows.map((r) => ({ subject: r.name, value: r.score })),
  };
}

// Comparaison patient / proche(s) : scores par driver côte à côte + données radar.
export function computeIdrComparison(patientAnswers, proches, lang = "fr") {
  const patient = computeIdrResults(patientAnswers || {}, lang);
  const procheResults = (proches || []).map((p) => ({
    name: p.name || "",
    results: computeIdrResults(p.answers || {}, lang),
  }));
  const radarData = DRIVERS.map((d, i) => {
    const row = { subject: patient.rows[i].name, patient: patient.rows[i].score };
    procheResults.forEach((pr, j) => { row[`proche${j}`] = pr.results.rows[i].score; });
    return row;
  });
  const rows = DRIVERS.map((d, i) => ({
    key: d.key,
    name: patient.rows[i].name,
    color: d.color,
    patient: patient.rows[i].score,
    proches: procheResults.map((pr) => pr.results.rows[i].score),
  }));
  return { radarData, rows, procheResults, patient };
}

export function idrClinicalReading(results, lang = "fr") {
  const { principal, gap, ecart } = results;
  const lowest = results.sorted[results.sorted.length - 1];
  const parts = [];
  parts.push(
    L(lang,
      `Votre driver principal est « ${principal.name} » (${principal.score} %), ce qui traduit un niveau ${principal.interp.label.toLowerCase()}.`,
      `Tu driver principal es «${principal.name}» (${principal.score} %), lo que refleja un nivel ${principal.interp.label.toLowerCase()}.`)
  );
  parts.push(
    L(lang,
      `L'écart avec le driver suivant est de ${gap} point${gap > 1 ? "s" : ""}, indiquant ${ecart}.`,
      `La diferencia con el driver siguiente es de ${gap} punto${gap > 1 ? "s" : ""}, lo que indica ${ecart}.`)
  );
  if (lowest.score <= 25) {
    parts.push(L(lang, `Le driver « ${lowest.name} » apparaît peu mobilisé (${lowest.score} %).`, `El driver «${lowest.name}» aparece poco movilizado (${lowest.score} %).`));
  }
  return parts.join(" ");
}
